import {
  ADD_ORDERS,
  ADD_REFUND_REQUEST_TO_ORDER,
  API_CALL,
  SET_GUEST_SPECIAL_REQUESTS,
  TOUR_TRAVELLER_DETAILS_RESET_SUBMISSION,
  TOUR_TRAVELLER_DETAILS_UPDATE_SUBMITTED,
  UPDATE_ORDER,
} from './actionConstants'

import {
  CANCEL_ORDER_GIFTING,
  CREATE_DATES_REQUEST,
  FETCH_ORDER,
  FETCH_ORDERS_DATES_REQUESTS,
  FETCH_ORDERS_FOR_CURRENT_USER,
  FETCH_PAYMENT_METHOD_STRIPE,
  FETCH_TOUR_ORDER_TRAVELLER_DETAILS,
  SUBMIT_ORDER_TRAVELLERS,
  SAVE_DRAFT_ORDER_TRAVELLERS,
  MARK_EXPERIENCE_BOOKING_DETAILS_AS_REDEEMED,
  FETCH_ORDER_ITEM_FLIGHT_DETAILS,
  UPDATE_ORDER_PARTNERSHIP, FETCH_FLIGHT_ORDER_PNRS,
  FETCH_ORDER_BY_SUBSCRIPTION,
  FETCH_ROOM_REFUND_INFO,
  UPDATE_DATES_REQUEST,
  FETCH_ORDER_CANCELLATION_CONFIGS,
} from './apiActionConstants'
import {
  cancelOrderGiftingRequest,
  createDatesRequestParams,
  getBrandOrders,
  getOrders,
  getOrderDatesRequest,
  getPaymentMethodFromStripe,
  getReservationDetailsForItems,
  markExperienceItemAsRedeemed,
  postDatesRequest,
  getOrderItemFlightDetails,
  updateOrderPartnership,
  getOrderById,
  getOrderBySubscriptionId,
  getPossibleRoomRefundTotal,
  updateDatesRequest,
  updateGuestSpecialRequests,
  getOrderItemsCancellationConfigs,
  getFullOrder,

} from 'api/order'
import {
  postOrderTravellerDetails,
  PostOrderTravellerDetailsParams,
  PostOrderTravellerDetailsPayload,
  reqGetTourOrderTravellerDetails,
  reqGetTourTravellerDetailsFromTourService,
  updateOrderTravellerDetailsToTourService,
} from 'api/traveller'
import { arrayToObject } from 'lib/array/arrayUtils'
import { getPaymentDetailsForOrders } from 'api/payment'
import { AppAction } from './types/ActionTypes'
import { TOUR_ORDER_TRAVELLER_DETAILS_TTL } from 'constants/tours'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { OrderItemType } from 'constants/order'
import { areOrdersBeingFetched } from 'lib/order/orderUtils'
import pollOrderService from 'lib/polling/pollOrderService'
import getObjectKey from 'lib/object/getObjectKey'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { isTourTravellerApiEnabled } from 'lib/tours/tourUtils'

export function fetchRoomRefundInfo(orderId: string, itemId: string, roomId?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = getObjectKey({ orderId, itemId, roomId })
    if (state.orders.itemRefundInfo[key]) {
      // already fetched it/processing it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ROOM_REFUND_INFO,
      request: () => getPossibleRoomRefundTotal(orderId, itemId, roomId),
      key,
    })
  }
}

export function fetchOrderBySubscriptionId(subscriptionId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const orderId = state.orders.ordersBySubscriptionId[subscriptionId]

    if (state.orders.orders[orderId] || state.orders.ordersFetching[subscriptionId]) {
      // already have the order or are fetching it, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_BY_SUBSCRIPTION,
      request: () => getOrderBySubscriptionId(subscriptionId),
      subscriptionId,
    })
  }
}
interface FetchOrderOptions {
  force?: boolean
}

export function fetchOrder(orderId: string, options?: FetchOrderOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      // don't refetch if:
      // we're already fetching it
      state.orders.ordersFetching[orderId] ||
      // we already have the full version
      (state.orders.orders[orderId]?.type === 'full' && !options?.force) ||
      // it errored
      !!state.orders.orderErrors[orderId]
    ) {
      return
    }

    const isLoyaltyEnabled = getIsLoyaltyEnabled(state)
    dispatch({
      type: API_CALL,
      api: FETCH_ORDER,
      request: () => getFullOrder(orderId, { isLoyaltyEnabled }),
      orderId,
    })
  }
}

export function updateOrder(updatedOrder: App.Order): AppAction {
  return {
    type: UPDATE_ORDER,
    order: updatedOrder,
    orderId: updatedOrder.id,
  }
}

interface TourOrderTravellerDetailsFetcherOptions {
  forceFetch?: boolean
}
export function fetchTourOrderTravellerDetails(tourOrderId: string, options?: TourOrderTravellerDetailsFetcherOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const orderDetails = state.orders.orders[tourOrderId]
    const existingOrderTravellerDetails = state.orders.tourOrdersTravellerDetails[tourOrderId]
    const shouldFetch = !!options?.forceFetch || !existingOrderTravellerDetails
    const hasFetchExpired = existingOrderTravellerDetails?.status === 'fetched' &&
      (existingOrderTravellerDetails.fetchedAtTimeStamp ?? 0) + TOUR_ORDER_TRAVELLER_DETAILS_TTL < new Date().getTime()

    if (shouldFetch || hasFetchExpired) {
      dispatch({
        type: API_CALL,
        api: FETCH_TOUR_ORDER_TRAVELLER_DETAILS,
        request: () => isTourTravellerApiEnabled() && orderDetails.tourItems.length > 0
          ? reqGetTourTravellerDetailsFromTourService(orderDetails.tourItems[0].bookingId)
          : reqGetTourOrderTravellerDetails(tourOrderId),
        tourOrderId,
      })
    }
  }
}

export function updateTourTravellerDetailsSubmitted(tourOrderId: string, travellerDetails: App.TourOrderTravellerDetails): AppAction {
  return {
    type: TOUR_TRAVELLER_DETAILS_UPDATE_SUBMITTED,
    travellerDetails,
    tourOrderId,
  }
}

export function resetTourTravellerDetailsSubmission(): AppAction {
  return {
    type: TOUR_TRAVELLER_DETAILS_RESET_SUBMISSION,
  }
}

export function submitTourTravellersDetails(
  payload: PostOrderTravellerDetailsPayload,
  params?: PostOrderTravellerDetailsParams,
): AppAction {
  return {
    type: API_CALL,
    api: params?.isDraft ? SAVE_DRAFT_ORDER_TRAVELLERS : SUBMIT_ORDER_TRAVELLERS,
    request: () => isTourTravellerApiEnabled() ? updateOrderTravellerDetailsToTourService(payload, params) : postOrderTravellerDetails(payload, params),
  }
}

interface FetchOrderParams {
  status?: 'upcoming'
  sort?: 'asc' | 'desc'
  itemType?: OrderItemType
  limit?: number
}

const StreamOrderPageSize = 20
export async function* fetchAndStreamOrders(
  memberId: string,
  currentRegionCode: string,
  params: FetchOrderParams = {},
) {
  let end = false
  let page = 1
  while (!end) {
    const results = await getOrders(memberId, currentRegionCode, {
      ...params,
      sort: params.sort ?? 'desc',
      page,
      pageSize: StreamOrderPageSize,
    })
    yield results.orders

    end = page * StreamOrderPageSize >= results.total
    page++
  }

  const brandOrders = await getBrandOrders(memberId)
  if (brandOrders.length > 0) {
    yield brandOrders
  }
}

export function fetchOrders(params: FetchOrderParams = {}): AppAction {
  return (dispatch, getState) => {
    let state = getState()
    const memberId = state.auth.account.memberId

    const isAlreadyFetching = areOrdersBeingFetched(state, params.status)
    if (isAlreadyFetching || !memberId) {
      // already happening, don't start it again
      return
    }

    dispatch({
      api: FETCH_ORDERS_FOR_CURRENT_USER,
      type: API_CALL,
      request: async() => {
        for await (const orders of fetchAndStreamOrders(memberId, state.geo.currentRegionCode, params)) {
          // while waiting for orders to stream, other things may have happened so get the latest state now
          state = getState()
          // pre-emptive dispatch of order data without their additioanl meta data
          // this improves preceived load times
          const missingOrders = orders.filter(order => !state.orders.orders[order.id] && !state.orders.ordersFetching[order.id])
          if (missingOrders.length) {
            dispatch({ type: ADD_ORDERS, orders: missingOrders })

            // pre-emptive orders dispatch, start fetching and filling in order meta-data
            const clonedOrders = missingOrders.map(order => ({ ...order }))

            // do this all non-blocking so we can continue streaming in orders
            // do not fail on a failure - it just won't update that portion
            const orderPromises = [
              getPaymentDetailsForOrders(clonedOrders.map(order => order.id)).then(paymentDetails => {
                clonedOrders.forEach(order => {
                  const orderDetails = paymentDetails[order.id]
                  if (orderDetails) {
                    order.payments = orderDetails.payments.filter(payment => payment.type !== 'manual') ?? []
                    order.depositDetails = orderDetails.depositDetails
                    order.instalmentDetails = orderDetails.instalmentDetails
                    order.paymentScheduleDetails = orderDetails.paymentScheduleDetails
                    order.reserveForZeroDetails = orderDetails.deferredPaymentDetails
                  }
                })
              }),
              ...clonedOrders.map(order => getReservationDetailsForItems(
                order.items.filter(item => item.reservationMade),
                order.id).then(reservations => {
                const reservationsById = arrayToObject(reservations, res => res.itemId)
                order.items = order.items.map(item => {
                  if (reservationsById[item.id]) {
                    return {
                      ...item,
                      reservation: reservationsById[item.id],
                    }
                  }
                  return item
                })
              })),
            ]

            Promise.allSettled(orderPromises).then(() => {
              dispatch({ type: ADD_ORDERS, orders: clonedOrders })
            })
          }
        }

        // For this action we're adding the orders to the state via ADD_ORDERS, so just return an empty array here
        return []
      },
      key: params.status || 'all',
    })
  }
}

/**
 * Some upcoming orders are upsellable based on the item types that it contains.
 * This action will fetch up to 5 upcoming orders for EACH upsellable item types.
 * Right now, only LE hotel item is upsellable, so it will return up to 5 orders.
 */
export function fetchUpsellableOrders(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId

    if (!memberId) {
      return
    }

    dispatch({
      api: FETCH_ORDERS_FOR_CURRENT_USER,
      type: API_CALL,
      request: () => getOrders(memberId, state.geo.currentRegionCode, {
        page: 1,
        pageSize: 5,
        itemType: OrderItemType.ACCOMMODATION,
        sort: 'asc',
      }).then(result => result.orders),
      key: 'upsellable',
    })
  }
}

export function createDatesRequest(body: createDatesRequestParams, onResolve: () => void): AppAction {
  return {
    type: API_CALL,
    api: CREATE_DATES_REQUEST,
    request: () => postDatesRequest(body)
      .then(() => {
        showSnackbar(
          'You will receive an email shortly confirming your request',
          'success',
          { heading: 'Your request has been successfully sent' },
        )
        onResolve()
      })
      .catch(e => {
        showSnackbar(
          e.message,
          'critical',
          { heading: 'Your date request has not been sent.' },
        )
        onResolve()
      }),
    body,
  }
}

export function fetchOrderDatesRequests(orderId: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_ORDERS_DATES_REQUESTS,
    request: () => getOrderDatesRequest(orderId),
    orderId,
  }
}

export function updateOrderDatesRequest(datesRequest: App.DatesRequest, status: string): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_DATES_REQUEST,
    request: () => updateDatesRequest(datesRequest.id, status),
    datesRequest,
  }
}

export function cancelOrderGifting(orderId: string): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: CANCEL_ORDER_GIFTING,
      request: () => cancelOrderGiftingRequest(orderId)
        .then(() => dispatch(fetchOrder(orderId, { force: true }))),
    })
  }
}

export function fetchOrderPaymentMethod(paymentMethodId: string, currency: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_PAYMENT_METHOD_STRIPE,
    request: () => getPaymentMethodFromStripe(paymentMethodId, currency),
  }
}

export function markExperienceOrderItemAsRedeemed(experienceItemId: string): AppAction {
  return {
    type: API_CALL,
    api: MARK_EXPERIENCE_BOOKING_DETAILS_AS_REDEEMED,
    request: () => {
      return new Promise<void>(resolve => {
        // easiest way to implement an 'undo' is to not do the action
        // until 'undo' is no longer available
        // so only actually mark the item as redeemed once the notification disappears
        const timeout = window.setTimeout(() => {
          markExperienceItemAsRedeemed(experienceItemId)
          resolve()
        }, 8000)

        showSnackbar('Your voucher has been marked as redeemed', 'success', {
          heading: 'Successfully redeemed',
          action: {
            label: 'Undo',
            onAction: () => {
              // prevent the timeout from firing
              window.clearTimeout(timeout)
              resolve()
            },
          },
        })
      })
    },
    experienceItemId,
  }
}

export function fetchOrderItemFlightDetails(orderId: string, itemId: string, skipCache: boolean = false): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.orders.flightDetails[itemId] && !skipCache) {
      // already fetched/are fetching, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_ITEM_FLIGHT_DETAILS,
      request: () => getOrderItemFlightDetails({ orderId, itemId }),
      key: itemId,
    })
  }
}

interface UpdateOrderPartnershipDetails {
  orderId: string
  type: string
  accountId: string
  lastName: string
  firstName: string
}
export function updateOrderPartnershipPostCheckout(updateOrderPartnershipDetails: UpdateOrderPartnershipDetails): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_ORDER_PARTNERSHIP,
    request: () => updateOrderPartnership(updateOrderPartnershipDetails),
  }
}

export function pollingOrderFlightsPnrId(order: App.Order): AppAction {
  return {
    type: API_CALL,
    api: FETCH_FLIGHT_ORDER_PNRS,
    request: () => pollOrderService({
      validateFunction: (order: App.Order) => {
        return order.flightItems.every((flightItem) => flightItem.pnrId && flightItem.status === 'completed') ? order : undefined
      },
      failedStatus: 'failed',
      apiCall: () => getOrderById(
        order.id,
      ),
      maxTime: 120000,
      waitTime: 2000,
    }),
  }
}

export function updateGuestSpecialRequestsPostCheckout(orderId: string, itemId: string, guestSpecialRequests: string): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: SET_GUEST_SPECIAL_REQUESTS,
      request: () => updateGuestSpecialRequests(orderId, itemId, guestSpecialRequests),
    })
  }
}

export function addRefundRequestToOrder(refundRequest: App.RefundRequest): AppAction {
  return {
    type: ADD_REFUND_REQUEST_TO_ORDER,
    orderId: refundRequest.orderId,
    orderItemId: refundRequest.itemId,
    refundRequest,
  }
}

export function fetchOrderCancellationConfigs(orderId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.orders.cancellationConfigsFetching[orderId] ||
      state.orders.cancellationConfigs[orderId] ||
      state.orders.cancellationConfigsErrors[orderId]
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_CANCELLATION_CONFIGS,
      request: () => getOrderItemsCancellationConfigs(orderId),
      orderId,
    })
  }
}
